import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import {
  ArrowRight,
  Palette,
  Video,
  Camera,
  Award,
  Users,
  Clock,
  CheckCircle,
  Sparkles,
  Layers,
  ShoppingBag,
  Plus,
  Minus,
  ZoomIn,
  Star,
} from 'lucide-react';

import Hero from '../components/Hero';
import Testimonials from '../components/Testimonials';
import { SERVICES, PROJECTS } from '../data';
import { BLOG_POSTS } from '../data/blogData';
import { SHOP_PRODUCTS, WHATSAPP_NUMBER } from '../data/shopData';

// ─── Custom hook: visibility for CountUp ───
const useVisibility = (threshold = 0.2) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isVisible];
};

// ─── Animation variants ───
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay } },
});

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 },
  },
};

const Home = () => {
  const featuredProjects = PROJECTS.slice(0, 3);
  const navigate = useNavigate();

  const recentPosts = BLOG_POSTS.slice(0, 3);
  const featuredProducts = SHOP_PRODUCTS.slice(0, 3);
  const featuredServices = SERVICES.slice(0, 3);

  const stats = [
    { value: 5, label: 'Years Experience', icon: Clock, suffix: '+' },
    { value: 120, label: 'Projects Delivered', icon: CheckCircle, suffix: '+' },
    { value: 100, label: 'Happy Clients', icon: Users, suffix: '+' },
    { value: 15, label: 'Awards Won', icon: Award, suffix: '+' },
  ];

  const iconMap = { Palette, Video, Camera, Layers };
  const [statsRef, statsVisible] = useVisibility(0.2);

  const goToPortfolio = (filter) => {
    navigate(`/portfolio?filter=${filter}`);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // ─── Shop state and handlers ───
  const [selectedSizes, setSelectedSizes] = useState({});
  const [quantities, setQuantities] = useState({});
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  const handleSizeChange = (productId, sizeIndex) => {
    setSelectedSizes(prev => ({
      ...prev,
      [productId]: sizeIndex,
    }));
  };

  const getSelectedSize = (product) => {
    const idx = selectedSizes[product.id];
    const sizeIndex = idx !== undefined ? idx : 0;
    return product.sizes[sizeIndex];
  };

  const getQuantity = (productId) => {
    return quantities[productId] || 1;
  };

  const updateQuantity = (productId, delta) => {
    setQuantities(prev => {
      const current = prev[productId] || 1;
      const newQty = Math.max(1, current + delta);
      return { ...prev, [productId]: newQty };
    });
  };

  const handleBuyNow = (product) => {
    const selectedSize = getSelectedSize(product);
    if (!selectedSize) {
      alert('Please select a size first.');
      return;
    }
    const qty = getQuantity(product.id);
    const total = selectedSize.price * qty;
    const message = `Hi, I want to order: ${product.name}\nSize: ${selectedSize.label}\nQuantity: ${qty}\nPrice: GHS ${selectedSize.price} × ${qty} = GHS ${total}`;
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const openLightbox = (image) => {
    setLightboxImage(image);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage(null);
  };

  return (
    <>
      <Hero />

      {/* ─── Services Section ─── */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={staggerContainer}
        className="py-24 bg-gradient-to-b from-black via-zinc-900 to-black relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-20 bg-gradient-to-r from-yellow-600/10 via-transparent to-yellow-600/10 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div variants={fadeUp()} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-300 text-sm font-medium tracking-wider uppercase">
              What I Do
            </span>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-white mt-4">
              My <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-500">Expertise</span>
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto text-lg">
              Visual Communication, From Concept to Execution.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {featuredServices.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <motion.div
                  key={index}
                  variants={fadeUp(0.1 * index)}
                  whileHover={{ y: -8, scale: 1.02 }}
                  className="group bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-yellow-500/40 transition-all duration-500 hover:shadow-2xl hover:shadow-yellow-500/10"
                >
                  <div className="relative">
                    <div className="absolute -inset-1 rounded-2xl bg-gradient-to-br from-yellow-500/0 to-yellow-500/0 group-hover:from-yellow-500/10 group-hover:to-yellow-500/10 transition-all duration-500 blur-xl" />
                    <div className="relative">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-500/20 to-yellow-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 group-hover:shadow-lg group-hover:shadow-yellow-500/20">
                        {Icon && <Icon size={28} className="text-yellow-400" />}
                      </div>
                      <h3 className="text-2xl font-semibold text-white mb-3">{service.title}</h3>
                      <p className="text-gray-400 leading-relaxed mb-4">{service.longDescription}</p>
                      <div className="mt-6 flex items-center justify-between">
                        <Link
                          to="/services"
                          className="text-sm text-yellow-400 hover:text-yellow-300 transition-colors flex items-center gap-1 group"
                        >
                          Learn More <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <button
                          onClick={() => goToPortfolio(service.filterValue || 'all')}
                          className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1 group"
                        >
                          View Portfolio <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-14">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-medium hover:shadow-2xl hover:shadow-yellow-500/30 transition-all duration-300 group"
            >
              Explore All Services
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </motion.section>

      {/* ─── Stats Section ─── */}
      <motion.section
        ref={statsRef}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={staggerContainer}
        className="py-16 bg-black border-y border-white/5"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeUp(0.1 * index)}
                  className="text-center p-6 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/5 hover:border-yellow-500/30 transition-all duration-300 hover:bg-white/10 hover:-translate-y-1"
                >
                  <div className="flex justify-center mb-3">
                    <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                      <Icon size={24} className="text-yellow-400" />
                    </div>
                  </div>
                  <div className="text-3xl font-bold text-white">
                    {statsVisible ? (
                      <CountUp end={stat.value} duration={2.5} suffix={stat.suffix} />
                    ) : (
                      <span>0{stat.suffix}</span>
                    )}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* ─── Featured Portfolio ─── */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={staggerContainer}
        className="py-24 bg-gradient-to-b from-black to-zinc-900"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUp()} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-300 text-sm font-medium tracking-wider uppercase">
              Portfolio
            </span>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-white mt-4">
              Featured <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-500">Work</span>
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto text-lg">
              A curated selection of my recent projects across graphic design, videography, and photography.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProjects.map((project, idx) => (
              <motion.div
                key={project.id}
                variants={fadeUp(0.1 * idx)}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group relative rounded-2xl overflow-hidden bg-white/5 border border-white/10 hover:border-yellow-500/40 transition-all duration-500 hover:shadow-2xl hover:shadow-yellow-500/10"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-6">
                  <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                    <div className="mt-4">
                      <Link
                        to={`/portfolio`}
                        className="inline-flex items-center gap-1 text-sm text-yellow-300 hover:text-white transition-colors group"
                      >
                        View Project <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-14">
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/20 text-white font-medium hover:bg-white/5 transition-all duration-300 group"
            >
              Explore Projects
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </motion.section>

      {/* ─── Blog Section (commented out – as in your original) ─── */}
      {/* ... */}

      {/* ─── Shop Section ─── */}
      <motion.section
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={staggerContainer}
        className="py-24 bg-gradient-to-b from-zinc-900 to-black"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUp()} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-300 text-sm font-medium tracking-wider uppercase">
              Shop
            </span>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold text-white mt-4">
              Frame <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-500">Collections</span>
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto text-lg">
              Beautiful frames to display your memories – from classic wood to modern metal, and digital templates too.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProducts.map((product, idx) => (
              <motion.div
                key={product.id}
                variants={fadeUp(0.1 * idx)}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 hover:border-yellow-500/40 transition-all duration-500 hover:shadow-2xl hover:shadow-yellow-500/10"
              >
                {/* ─── Image with rounded edges ─── */}
                <div className="relative aspect-[4/3] overflow-hidden rounded-2xl m-4 cursor-pointer">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                    onClick={() => openLightbox(product.image)}
                  />
                  {/* ─── Zoom hint on hover ─── */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center pointer-events-none">
                    <span className="px-4 py-2 rounded-full bg-yellow-500/80 text-white text-sm font-medium backdrop-blur-sm flex items-center gap-2">
                      <ZoomIn size={16} /> Click to enlarge
                    </span>
                  </div>
                </div>

                {/* ─── Product details below image ─── */}
                <div className="px-4 pb-4">
                  {/* <h3 className="text-xl font-semibold text-white">{product.name}</h3> */}

                  {/* ─── Size selector ─── */}
                  <div className="mt-3">
                    <label className="text-xs text-gray-400 uppercase tracking-wider block mb-1.5">
                      Select Size
                    </label>
                    <select
                      value={selectedSizes[product.id] ?? 0}
                      onChange={(e) => handleSizeChange(product.id, parseInt(e.target.value))}
                      className="w-full bg-black/60 text-white border border-white/20 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500/20 transition-all duration-200"
                    >
                      {product.sizes.map((size, idx) => (
                        <option key={idx} value={idx}>
                          {size.label} – GHS {size.price}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* ─── Price, stock, quantity ─── */}
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl font-bold text-yellow-400">
                          GHS {getSelectedSize(product).price}
                        </span>
                        <span className="flex items-center gap-1 text-green-400 text-xs">
                          <CheckCircle size={12} /> In Stock
                        </span>
                      </div>
                    </div>

                    {/* ─── Quantity selector ─── */}
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400 uppercase tracking-wider">Qty</span>
                      <div className="flex items-center gap-1 bg-black/40 rounded-lg border border-white/10">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updateQuantity(product.id, -1);
                          }}
                          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-yellow-500/20 rounded-l-lg transition"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="w-8 text-center text-white text-sm font-medium">
                          {getQuantity(product.id)}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            updateQuantity(product.id, 1);
                          }}
                          className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white hover:bg-yellow-500/20 rounded-r-lg transition"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>

                    {/* ─── Buy Now button ─── */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBuyNow(product);
                      }}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-white text-sm font-medium hover:shadow-lg hover:shadow-yellow-500/30 transition-all duration-300"
                    >
                      <ShoppingBag size={16} /> Buy Now
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-14">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/20 text-white font-medium hover:bg-white/5 transition-all duration-300 group"
            >
              Explore All Frames
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </motion.section>

      {/* ─── Lightbox ─── */}
      {lightboxOpen && lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 text-gray-400 hover:text-white text-sm flex items-center gap-2 transition"
            >
              Close ✕
            </button>
            <img
              src={lightboxImage}
              alt="Product preview"
              className="w-full h-auto max-h-[85vh] object-contain rounded-2xl"
            />
          </div>
        </div>
      )}

      {/* ─── Testimonials ─── */}
      <Testimonials />

      {/* ─── Call-to-Action ─── */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="py-24 bg-gradient-to-b from-zinc-900 to-black relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-10 bg-gradient-to-r from-yellow-600/20 via-transparent to-yellow-600/20 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-yellow-600/10 blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-yellow-600/10 blur-3xl animate-pulse delay-1000" />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-300 text-sm font-medium tracking-wider uppercase">
            Get in Touch
          </span>
          <h2 className="font-serif text-4xl sm:text-5xl font-bold text-white mt-4">
            Let’s create <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-500">something</span> meaningful
          </h2>
          <p className="text-gray-400 mt-4 max-w-2xl mx-auto text-lg">
            Whether you need a visual campaign, brand content, photography, video, motion design, or a creative partner to help shape an idea, I’m always open to exploring new creative challenges.
          </p>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 mt-10 px-10 py-4 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-medium text-lg hover:shadow-2xl hover:shadow-yellow-500/40 transition-all duration-300 group"
            >
              Start a Conversation
              <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </motion.section>
    </>
  );
};

export default Home;